import * as React from "react";
import {
    Box,
    CircularProgress,
    Paper,
    Stack,
    Typography,
    Alert,
    Button,
    Divider,
} from "@mui/material";
import { useNavigate } from "react-router";
import PageContainer from "./PageContainer";
import { getMatchingJobs } from "../../services/matchingService";
import { applyToJob } from "../../services/applicationService"; // <-- make sure this exists
import useNotifications from "../hooks/useNotifications/useNotifications";

const RenderBoxItems = ({ items }) => {
    if (!items || items.length === 0) return null;
    return (
        <Stack direction="row" flexWrap="wrap" sx={{ mt: 1 }}>
            {items.map((item, idx) => (
                <Box
                    key={idx}
                    sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "primary.main",
                        color: "primary.main",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        m: 0.5,
                    }}
                >
                    {item}
                </Box>
            ))}
        </Stack>
    );
};

export default function ShortlistJobPage() {
    const navigate = useNavigate();
    const notifications = useNotifications();

    const [results, setResults] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    // Track applied status per job
    const [appliedJobs, setAppliedJobs] = React.useState({});
    const [applyingJobId, setApplyingJobId] = React.useState(null);

    React.useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                const body = { topN: 10 };
                const response = await getMatchingJobs(body);
                setResults(response?.message?.results || []);
            } catch (err) {
                setError(err);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    const handleApply = async (jobId) => {
        if (!jobId || appliedJobs[jobId]) return;

        setApplyingJobId(jobId);
        try {
            const response = await applyToJob(jobId);
            console.log("Apply response:", response);

            if (response.success) {
                setAppliedJobs((prev) => ({ ...prev, [jobId]: true }));
                notifications.show("Applied successfully!", {
                    severity: "success",
                    autoHideDuration: 3000,
                });
            } else {
                notifications.show(response.message || "Failed to apply", {
                    severity: "error",
                    autoHideDuration: 3000,
                });
            }
        } catch (err) {
            notifications.show(`Failed to apply: ${err.message}`, {
                severity: "error",
                autoHideDuration: 3000,
            });
        } finally {
            setApplyingJobId(null);
        }
    };

    if (isLoading) {
        return (
            <PageContainer title="Matching Jobs">
                <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress />
                </Box>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer title="Matching Jobs">
                <Alert severity="error">{error.message}</Alert>
            </PageContainer>
        );
    }

    return (
        <PageContainer
            title="Matching Jobs"
            breadcrumbs={[
                { title: "My Resume", path: "/app/user/resume" },
                { title: "Matching Jobs" },
            ]}
        >
            <Stack spacing={3}>
                {results.map((job) => {
                    const applied = appliedJobs[job.jobId] || false;
                    const isApplying = applyingJobId === job.jobId;

                    // Decide color by score range
                    let scoreColor = "grey";
                    if (job.score === 100) {
                        scoreColor = "green";
                    } else if (job.score >= 70) {
                        scoreColor = "blue";
                    } else if (job.score >= 40) {
                        scoreColor = "orange";
                    } else {
                        scoreColor = "red";
                    }

                    return (
                        <Paper
                            key={job.jobId}
                            sx={{
                                p: 3,
                                position: "relative",
                            }}
                            elevation={2}
                        >
                            {/* Score badge on top right */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 16,
                                    right: 16,
                                    border: `2px solid ${scoreColor}`,
                                    borderRadius: "8px",
                                    px: 1.5,
                                    py: 0.5,
                                    fontWeight: "bold",
                                    color: scoreColor,
                                    minWidth: 60,
                                    textAlign: "center",
                                }}
                            >
                                {job.score}%
                            </Box>

                            {/* Job details */}
                            <Typography variant="h6">{job.jobTitle}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {job.department} • {job.location}
                            </Typography>

                            {job.matchedSkills?.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ mt: 2 , fontWeight: "bold" }}>
                                        Matched Skills
                                    </Typography>
                                    <RenderBoxItems items={job.matchedSkills} />
                                </>
                            )}

                            {job.missingSkills?.length > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ mt: 1 , fontWeight: "bold"}}>
                                        Missing Skills
                                    </Typography>
                                    <RenderBoxItems items={job.missingSkills} />
                                </>
                            )}

                            {job.summary && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {job.summary}
                                </Typography>
                            )}
                            
                            <Divider sx={{my:1.5}}/>
                            {/* Apply button */}
                            <Stack direction="row" spacing={2} justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    color={applied ? "success" : "primary"}
                                    onClick={() => handleApply(job.jobId)}
                                    disabled={isApplying || applied}
                                >
                                    {applied ? "Applied" : isApplying ? "Applying..." : "Apply"}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => navigate(`/app/job/${job.jobId}`)}
                                >
                                    View job
                                </Button>
                            </Stack>
                        </Paper>
                    );
                })}

            </Stack>
        </PageContainer>
    );
}
